import React, { useState, useEffect, useCallback } from "react";
import { Form, Input, Button, message, Spin } from "antd";
import {
	LoadingOutlined,
	UserOutlined,
	MailOutlined,
	PhoneOutlined,
} from "@ant-design/icons";
import {
	getUserProfile,
	updateUserProfile,
	createPortalSession,
	getSubscriptionStatus,
} from "../../utils/api";

const Profile = () => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [user, setUser] = useState(null);
	const [activeTab, setActiveTab] = useState("profile");
	const [subscription, setSubscription] = useState(null);

	const fetchProfile = useCallback(async () => {
		setLoading(true);
		const res = await getUserProfile();
		if (res?.data?.statusCode === 200) {
			setUser(res.data.data);
			form.setFieldsValue({
				name: res.data.data.name,
				email: res.data.data.email,
				phone: res.data.data.phone,
			});
		} else {
			message.error("Failed to load profile");
		}
		setLoading(false);
	}, [form]);

	const fetchSubscription = useCallback(async () => {
		const res = await getSubscriptionStatus();
		if (res?.data?.statusCode === 200 && res?.data?.data?.subscription) {
			setSubscription(res.data.data.subscription);
		}
	}, []);

	useEffect(() => {
		fetchProfile();
		fetchSubscription();
	}, [fetchProfile, fetchSubscription]);

	const handleUpdateProfile = async (values) => {
		setSaving(true);
		const res = await updateUserProfile({
			name: values.name,
			phone: values.phone,
		});
		if (res?.data?.statusCode === 200) {
			message.success("Profile updated successfully");
			setUser(res.data.data);
		} else {
			message.error(res?.data?.message || "Failed to update profile");
		}
		setSaving(false);
	};

	const handleManageSubscription = async () => {
		const res = await createPortalSession();
		if (res?.data?.data?.url) {
			window.location.href = res.data.data.url;
		} else {
			message.error("Failed to open subscription portal");
		}
	};

	if (loading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
				<Spin
					indicator={
						<LoadingOutlined style={{ fontSize: 48, color: "#22d3ee" }} spin />
					}
				/>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 pt-24 pb-12">
			<div className="container mx-auto px-4 max-w-4xl">
				{/* Header */}
				<div className="text-center mb-10">
					<div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 mb-4">
						{user?.profilePicture ? (
							<img
								src={user.profilePicture}
								alt={user.name}
								className="w-20 h-20 rounded-full object-cover"
							/>
						) : (
							<span className="text-3xl font-bold text-white">
								{user?.name?.charAt(0)?.toUpperCase() || "U"}
							</span>
						)}
					</div>
					<h1 className="text-3xl font-bold text-white mb-2">{user?.name}</h1>
					<p className="text-gray-400">{user?.email}</p>
				</div>

				{/* Tab Navigation */}
				<div className="flex justify-center gap-4 mb-8">
					<button
						onClick={() => setActiveTab("profile")}
						className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
							activeTab === "profile"
								? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
								: "bg-white/10 text-white hover:bg-white/20"
						}`}
					>
						Profile Settings
					</button>
					{user?.role !== "manager" && (
						<button
							onClick={() => setActiveTab("subscription")}
							className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
								activeTab === "subscription"
									? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
									: "bg-white/10 text-white hover:bg-white/20"
							}`}
						>
							Subscription
						</button>
					)}
				</div>

				{/* Profile Settings Tab */}
				{activeTab === "profile" && (
					<div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/10">
						<h2 className="text-xl font-semibold text-white mb-6">
							Personal Information
						</h2>
						<Form
							form={form}
							layout="vertical"
							onFinish={handleUpdateProfile}
							className="profile-form"
						>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<Form.Item
									name="name"
									label={<span className="text-gray-300">Full Name</span>}
									rules={[
										{ required: true, message: "Please enter your name" },
									]}
								>
									<Input
										prefix={<UserOutlined className="text-gray-400" />}
										placeholder="Enter your name"
										className="h-12 bg-white/5 border-white/20 text-white placeholder:text-gray-500 rounded-lg"
									/>
								</Form.Item>

								<Form.Item
									name="email"
									label={<span className="text-gray-300">Email Address</span>}
								>
									<Input
										prefix={<MailOutlined className="text-gray-400" />}
										placeholder="Email"
										disabled
										className="h-12 bg-white/5 border-white/20 text-gray-400 rounded-lg"
									/>
								</Form.Item>

								<Form.Item
									name="phone"
									label={<span className="text-gray-300">Phone Number</span>}
								>
									<Input
										prefix={<PhoneOutlined className="text-gray-400" />}
										placeholder="Enter your phone number"
										className="h-12 bg-white/5 border-white/20 text-white placeholder:text-gray-500 rounded-lg"
									/>
								</Form.Item>

								<Form.Item
									label={<span className="text-gray-300">Account Type</span>}
								>
									<div className="h-12 flex items-center px-4 bg-white/5 border border-white/20 rounded-lg">
										<span
											className={`px-3 py-1 rounded-full text-sm font-medium ${
												user?.authProvider === "google"
													? "bg-red-500/20 text-red-300"
													: "bg-blue-500/20 text-blue-300"
											}`}
										>
											{user?.authProvider === "google"
												? "Google Account"
												: "Email Account"}
										</span>
									</div>
								</Form.Item>
							</div>

							<div className="mt-8 flex justify-end">
								<Button
									type="primary"
									htmlType="submit"
									loading={saving}
									className="h-12 px-8 bg-gradient-to-r from-cyan-500 to-blue-600 border-none rounded-lg font-medium hover:from-cyan-600 hover:to-blue-700"
								>
									Save Changes
								</Button>
							</div>
						</Form>
					</div>
				)}

				{/* Subscription Tab */}
				{activeTab === "subscription" && user?.role !== "manager" && (
					<div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/10">
						<h2 className="text-xl font-semibold text-white mb-6">
							Subscription Management
						</h2>
						<div className="text-center py-8">
							<div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mb-4">
								<svg
									className="w-8 h-8 text-green-400"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M5 13l4 4L19 7"
									/>
								</svg>
							</div>
							<h3 className="text-lg font-medium text-white mb-2">
								Active Subscription
							</h3>
							{subscription?.isCodeBased ? (
								<>
									<p className="text-gray-400 mb-2">
										Your subscription was activated using a subscription code.
									</p>
									{subscription?.currentPeriodEnd && (
										<p className="text-gray-500 text-sm mb-6">
											Expires on:{" "}
											{new Date(
												subscription.currentPeriodEnd,
											).toLocaleDateString()}
										</p>
									)}
									<div className="py-4 px-6 bg-white/5 border border-white/10 text-gray-400 rounded-xl inline-block">
										<p className="text-sm">
											To manage your subscription, please contact admin
										</p>
									</div>
								</>
							) : (
								<>
									<p className="text-gray-400 mb-6">
										Manage your subscription, update payment methods, or view
										billing history through the Stripe Customer Portal.
									</p>
									<Button
										onClick={handleManageSubscription}
										className="h-12 px-8 bg-gradient-to-r from-cyan-500 to-blue-600 border-none rounded-lg font-medium text-white hover:from-cyan-600 hover:to-blue-700"
									>
										Manage Subscription
									</Button>
								</>
							)}
						</div>
					</div>
				)}

				{/* Account Info */}
				<div className="mt-8 bg-white/5 rounded-xl p-6 border border-white/10">
					<h3 className="text-sm font-medium text-gray-400 mb-4">
						Account Details
					</h3>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
						<div>
							<p className="text-gray-500 text-sm">Member Since</p>
							<p className="text-white font-medium">
								{user?.createdAt
									? new Date(user.createdAt).toLocaleDateString("en-US", {
											month: "short",
											year: "numeric",
										})
									: "-"}
							</p>
						</div>
						<div>
							<p className="text-gray-500 text-sm">Email Verified</p>
							<p
								className={`font-medium ${user?.isEmailVerified ? "text-green-400" : "text-yellow-400"}`}
							>
								{user?.isEmailVerified ? "Verified" : "Pending"}
							</p>
						</div>
						<div>
							<p className="text-gray-500 text-sm">Account Type</p>
							<p className="text-white font-medium capitalize">
								{user?.role || "Standard"}
							</p>
						</div>
						<div>
							<p className="text-gray-500 text-sm">Auth Provider</p>
							<p className="text-white font-medium capitalize">
								{user?.authProvider || "Email"}
							</p>
						</div>
					</div>
				</div>
			</div>

			<style jsx="true">{`
        .profile-form .ant-input {
          background: rgba(255, 255, 255, 0.05) !important;
          border-color: rgba(255, 255, 255, 0.2) !important;
          color: white !important;
        }
        .profile-form .ant-input:hover,
        .profile-form .ant-input:focus {
          border-color: rgba(34, 211, 238, 0.5) !important;
        }
        .profile-form .ant-input-disabled {
          color: rgba(255, 255, 255, 0.5) !important;
        }
        .profile-form .ant-input-prefix {
          color: rgba(255, 255, 255, 0.4) !important;
        }
      `}</style>
		</div>
	);
};

export default Profile;
